import { evaluatePlatformReadiness } from '../../../shared/posting-readiness';
import { needsOutcomeResolution } from '../../../shared/provider-outcome';
import { BaseService } from '../base/BaseService';
import { ServiceError } from '../base/ServiceError';
import type { ServiceResponse } from '../base/ServiceTypes';

import {
  LINKEDIN_DESTINATION_REQUIRED,
  LINKEDIN_DESTINATION_REQUIRED_MESSAGE,
  linkedInDestinationChoiceMissing,
  requestLinkedInDestinationChoice,
} from '@/lib/connections/destination-options';
import { resolveConnectionGuard } from '@/lib/connections/getActivePlatformConnection';
import type { PlatformId } from '@/lib/platforms/registry';
import { resolvePlatformCaption } from '@/lib/posts/platform-caption';
import { PREVIEW_ACCOUNT_MESSAGE, isPreviewAccountFlagged } from '@/lib/preview-account';

export type PublishPlatformStatus = 'published' | 'failed' | 'scheduled' | 'skipped';

/** One platform's delivery truth, as reported by the schedule-post edge fn. */
export interface PublishPlatformResult {
  platform: string;
  status: PublishPlatformStatus;
  platformPostId?: string | null;
  permalink?: string | null;
  error?: string | null;
  /**
   * The provider reported success but returned no usable post id. Rides on
   * `status:'failed'` so it is never counted as a delivery, but it must be
   * reported as "couldn't confirm", NEVER as "publishing failed" — the post is
   * very likely live.
   */
  unconfirmed?: boolean;
}

/**
 * Whether the dispatch delivered everywhere it tried.
 *
 * `partial` is the case this type exists for: schedule-post rolls a row up to
 * `partially_published` / `partially_scheduled`, neither of which is `failed`,
 * so the old contract returned them as an unqualified success and the UI said
 * "Posted" for platforms that never received the post. `all_published` is also
 * the back-compat default when the response carries no `results` array — that
 * reproduces the previous behavior exactly for older deployments.
 * `unconfirmed` is the provider-said-yes-but-gave-no-id case.
 */
export type PublishOutcome = 'all_published' | 'partial' | 'held' | 'unconfirmed';

export interface UnresolvedPublishTarget {
  id: string;
  platform: string;
  deliveryStatus: 'native_status_unknown' | 'manual_action_required';
}

export interface OpenPublishTargets {
  unresolved: UnresolvedPublishTarget[];
  owedPlatforms: string[];
}

export interface PublishNowResult {
  postId: string;
  /**
   * @deprecated First entry of `platforms` — kept so single-platform callers
   * that read `.platform` (and the older agent tool response shape) don't
   * need to change. New code should read `platforms`.
   */
  platform: PlatformId;
  /**
   * Every platform THIS call actually dispatched to — i.e. the
   * caller-requested set AFTER the client-side readiness partition (P0-1)
   * dropped any not-ready platform. Never equal to the caller's requested
   * list when at least one requested platform was unready.
   */
  platforms: PlatformId[];
  platformPostId?: string;
  publishedAt: string;
  /** True when the trust gate held the post for owner/HQ review (not published). */
  held?: boolean;
  outcome: PublishOutcome;
  /**
   * The FULL per-platform array from the edge fn, covering the row's entire
   * intended platform set merged with prior attempts — i.e. it includes rows
   * this action never touched. Kept for reporting; never use it directly to
   * judge this action. Absent when the response predates the contract.
   */
  results?: PublishPlatformResult[];
  /** Platforms THIS call delivered to. Falls back to `platforms`. */
  publishedPlatforms: string[];
  /** Platforms THIS call attempted that hard-failed. Never skipped, never unconfirmed. */
  failedPlatforms: string[];
  /** Platforms THIS call may have delivered to but cannot prove. */
  unconfirmedPlatforms: string[];
  /**
   * Platforms the caller asked for that were dropped BEFORE dispatch because
   * `evaluatePlatformReadiness` (or the connection guard) says they are not
   * ready — e.g. one platform's caption is over its limit. Never contains a
   * platform that was actually attempted; those show up in `failedPlatforms`
   * / `unconfirmedPlatforms` instead. Empty when every requested platform was
   * ready.
   */
  skippedUnreadyPlatforms: string[];
}

/**
 * PublishService — chat-free immediate publish (A3).
 *
 * Mirrors the agent `publish_now` tool (src/lib/ai-agent/tools/publish-now.ts)
 * so the "Post now" button in the inspector and the agent tool share ONE
 * semantics: verify a live connection, flip the post to `publishing`, POST to
 * the `schedule-post` edge function with `scheduledTime: null`, and revert to
 * `draft` (with error_message) on failure.
 *
 * IMPORTANT: this is NOT `PostService.publishPost`, which only stamps
 * status='published' in the DB WITHOUT delivering to any platform. That method
 * is a record-keeping flip; this method actually dispatches the post. "Post
 * now" must use this service or it will silently mark posts published that were
 * never sent.
 */
export class PublishService extends BaseService {
  private static instance: PublishService;

  private constructor() {
    super({ defaultTTL: 0, enableCache: false, enableRetry: false });
  }

  public static getInstance(): PublishService {
    if (!PublishService.instance) {
      PublishService.instance = new PublishService();
    }
    return PublishService.instance;
  }

  /**
   * Publish a post to one or more connected platforms immediately.
   *
   * P0-1 fix (client-side readiness partition): `evaluatePlatformReadiness` is
   * pure/synchronous and already shared with the server, so we compute the
   * ready subset of the CALLER's requested platforms here and send only that
   * subset in one atomic `schedule-post` call. This is a cheap, low-risk
   * substitute for a server-side partition rewrite — `schedule-post`'s own
   * pre-dispatch gates stay all-or-nothing (see plan §4 P0-1), so any platform
   * we didn't already exclude here would still throw for the WHOLE request.
   *
   * What this partition does NOT cover: server-only truth the client can't
   * (fully) recompute — `perPlatformConfigFor` reading `media_keys` server-
   * side, and `selectUnambiguousConnections`' own ambiguity check inside
   * `schedule-post`. Those still hard-fail the batch at the edge function if
   * they somehow diverge from what we saw here, and that surfaces as a normal
   * thrown error — never a silent drop.
   *
   * @param postId    The social_posts row to publish.
   * @param platform  Lowercase platform id, or a list of them (each must have
   *                  an active user_connection). A bare platform id is kept
   *                  working for every existing single-platform call site.
   */
  /**
   * The active targets of a post the editor must explain: those waiting on a provider check or the author, and those
   * still owed a send. RLS scopes the read.
   */
  async getOpenTargets(postId: string): Promise<ServiceResponse<OpenPublishTargets>> {
    return this.execute(async () => {
      const { data, error } = await this.client
        .from('social_post_targets')
        .select('id, platform, delivery_status, error_code')
        .eq('post_id', postId)
        .eq('readiness_snapshot->>active', 'true')
        .in('delivery_status', [
          'native_status_unknown',
          'manual_action_required',
          'unscheduled',
          'failed',
        ]);
      if (error) throw ServiceError.fromPostgrestError(error);
      const rows = data ?? [];
      return {
        unresolved: rows.filter(needsOutcomeResolution).map((row) => ({
          id: row.id,
          platform: row.platform,
          deliveryStatus: row.delivery_status as UnresolvedPublishTarget['deliveryStatus'],
        })),
        owedPlatforms: rows
          .filter(
            (row) => row.delivery_status === 'unscheduled' || row.delivery_status === 'failed',
          )
          .map((row) => row.platform),
      };
    });
  }

  async publishNow(
    postId: string,
    platform: PlatformId | PlatformId[],
    options: {
      /** The platforms the author checked and wants sent again although their outcome is unknown. */
      confirmRepostPlatforms?: string[];
      /** A sibling of a target being checked: the row's status describes that target, not this send. */
      sendingOwedPlatforms?: boolean;
    } = {},
  ): Promise<ServiceResponse<PublishNowResult>> {
    return this.execute(async () => {
      if (isPreviewAccountFlagged()) {
        throw new ServiceError('PREVIEW_ACCOUNT', PREVIEW_ACCOUNT_MESSAGE);
      }
      // social_posts + user_connections are OWNER-SCOPED (RLS: user_id = current_account_owner_id()).
      const userId = await this.ensureAuthenticatedOwner();

      const requestedPlatformsInput: PlatformId[] = Array.isArray(platform) ? platform : [platform];
      if (requestedPlatformsInput.length === 0) {
        throw new ServiceError('POST_NOT_READY', 'No platform was selected to publish.');
      }

      const { data: post, error: postErr } = await this.client
        .from('social_posts')
        .select(
          'id, content, platform_captions, media_urls, platforms, user_id, status, media_type, video_duration_seconds, video_aspect_ratio, media_keys, destination_connection_ids, destination_resource_ids, brand_kit_id',
        )
        .eq('id', postId)
        .eq('user_id', userId)
        .maybeSingle();

      if (postErr) throw ServiceError.fromPostgrestError(postErr);
      if (!post) throw ServiceError.notFound('Post');
      if (post.status === 'published') {
        throw new ServiceError('ALREADY_PUBLISHED', 'This post has already been published.');
      }

      // Additive, nullable per-platform destination override (col comment on
      // social_posts.destination_connection_ids): a missing key or an explicit
      // JSON null both mean "no override" — resolve via the normal ambiguity
      // gate. A present id is a hard requirement, never a silent fallback.
      const destinationOverrides =
        post.destination_connection_ids &&
        typeof post.destination_connection_ids === 'object' &&
        !Array.isArray(post.destination_connection_ids)
          ? (post.destination_connection_ids as Record<string, string | null>)
          : null;
      const destinationOverrideFor = (p: string): string | null | undefined =>
        destinationOverrides?.[p];

      const perPlatformMediaConfig = (
        p: string,
      ): { aspectRatio?: string; variantUrl?: string } | null =>
        post.media_keys && typeof post.media_keys === 'object' && !Array.isArray(post.media_keys)
          ? (((post.media_keys as { perPlatform?: Record<string, unknown> }).perPlatform?.[p] ??
              null) as { aspectRatio?: string; variantUrl?: string } | null)
          : null;

      if (
        requestedPlatformsInput.includes('linkedin' as PlatformId) &&
        (await linkedInDestinationChoiceMissing(this.client, post))
      ) {
        requestLinkedInDestinationChoice({
          postId,
          actionLabel: 'Post now',
          retry: async () =>
            (await this.publishNow(postId, platform, options)).error?.message ?? null,
        });
        throw new ServiceError(
          LINKEDIN_DESTINATION_REQUIRED,
          LINKEDIN_DESTINATION_REQUIRED_MESSAGE,
        );
      }

      type ConnectionFailure = { code: string; message: string };
      const connectionFailures = new Map<string, ConnectionFailure>();
      const readinessByPlatform = new Map<string, ReturnType<typeof evaluatePlatformReadiness>>();

      for (const p of requestedPlatformsInput) {
        const connectionGuard = await resolveConnectionGuard(
          this.client,
          userId,
          p,
          destinationOverrideFor(p),
        );
        if (!connectionGuard.connected) {
          const code =
            connectionGuard.reason === 'not_connected'
              ? 'PLATFORM_NOT_CONNECTED'
              : connectionGuard.reason === 'needs_reconnect'
                ? 'PLATFORM_NEEDS_RECONNECT'
                : connectionGuard.reason === 'ambiguous'
                  ? 'PLATFORM_AMBIGUOUS'
                  : connectionGuard.reason === 'invalid_override'
                    ? 'PLATFORM_DESTINATION_UNAVAILABLE'
                    : 'CONNECTION_LOOKUP_FAILED';
          // Connection ambiguity / lookup failure / an invalid recorded
          // destination are all server-only truth this partition can't safely
          // paper over — ambiguous means we genuinely don't know WHICH
          // connection would receive the post, and an invalid override means
          // silently re-resolving could publish as a DIFFERENT account than
          // the post says (public, irreversible). Hard-fail the whole call
          // immediately rather than silently dropping it.
          if (
            connectionGuard.reason === 'ambiguous' ||
            connectionGuard.reason === 'error' ||
            connectionGuard.reason === 'invalid_override'
          ) {
            throw new ServiceError(code, connectionGuard.message);
          }
          // not_connected / needs_reconnect: this is exactly what
          // `evaluatePlatformReadiness`'s own `connected: false` branch
          // already flags as a blocked issue, so let it flow through the
          // normal readiness partition below (silently excluded from the
          // ready subset unless it's the ONLY requested platform).
          connectionFailures.set(p, { code, message: connectionGuard.message });
        }
        const connection = connectionGuard.connected ? connectionGuard.connection : null;

        readinessByPlatform.set(
          p,
          evaluatePlatformReadiness({
            platform: p,
            caption: resolvePlatformCaption(post.content, post.platform_captions, p),
            mediaUrls: post.media_urls ?? [],
            mediaType: post.media_type,
            connected: connectionGuard.connected,
            // The row's status says what the last send did. `failed` or `publishing` would refuse this send outright,
            // and the server decides whether it may go ahead, so these use the same value it does.
            rowStatus:
              options.confirmRepostPlatforms?.length || options.sendingOwedPlatforms
                ? 'draft'
                : post.status,
            videoDurationSeconds: post.video_duration_seconds ?? null,
            // Activate the IG Reel 9:16 aspect gate client-side too (was dropped).
            videoAspectRatio: post.video_aspect_ratio ?? null,
            // A5: readiness fails CLOSED without the connection's account_type —
            // omitting it blocked Story publishes even for BUSINESS accounts.
            accountType: connection?.account_type ?? null,
            perPlatform: perPlatformMediaConfig(p),
          }),
        );
      }

      const readyPlatforms = requestedPlatformsInput.filter(
        (p) => readinessByPlatform.get(p)?.canPublish === true,
      );
      const skippedUnreadyPlatforms = requestedPlatformsInput.filter(
        (p) => !readyPlatforms.includes(p),
      );

      if (readyPlatforms.length === 0) {
        // Nothing to send. Preserve the specific connection-guard error code
        // (PLATFORM_NOT_CONNECTED etc.) when that's why the FIRST requested
        // platform is unready — this is what every existing single-platform
        // caller already depends on — and fall back to the generic readiness
        // message otherwise.
        const first = requestedPlatformsInput[0];
        const connectionFailure = connectionFailures.get(first);
        if (connectionFailure) {
          throw new ServiceError(connectionFailure.code, connectionFailure.message);
        }
        const readiness = readinessByPlatform.get(first);
        throw new ServiceError(
          'POST_NOT_READY',
          readiness?.blockingIssues[0]?.message ?? `${first} is not ready to publish.`,
        );
      }

      // Per-platform caption override ?? shared content, for the first ready
      // platform. `schedule-post` recomputes the real per-connection caption
      // itself from the persisted row, so this only matters as the fallback
      // used on a fresh-insert path publishNow never takes (it always passes
      // an existing postId).
      const requestContent = resolvePlatformCaption(
        post.content,
        post.platform_captions,
        readyPlatforms[0],
      );

      // NO optimistic status flip here. The schedule-post edge function OWNS
      // the draft->publishing transition atomically (with the re-arm guard as
      // its concurrency precondition). Pre-flipping to 'publishing' made the
      // edge fn's re-arm guard reject our own write ("already publishing") and
      // 400'd every immediate publish. The UI reflects in-flight state via the
      // mutation's isPending; the row's real status comes back on invalidation.
      const { data, error: fnErr } = await this.client.functions.invoke<{
        socialPostId?: string;
        platformPostId?: string | null;
        status?: string;
        held?: boolean;
        results?: PublishPlatformResult[];
      }>('schedule-post', {
        body: {
          postId,
          platforms: readyPlatforms,
          content: requestContent,
          mediaUrls: post.media_urls || [],
          scheduledTime: null,
          isAIGC: false,
          ...(options.confirmRepostPlatforms?.length
            ? {
                confirmRepostPlatforms: options.confirmRepostPlatforms.filter((p) =>
                  (readyPlatforms as string[]).includes(p),
                ),
              }
            : {}),
        },
      });

      const platformList = readyPlatforms.join(', ');

      if (fnErr) {
        // The edge function owns the post lifecycle: on its own throws it
        // already reverted status + wrote a specific error_message, and on a
        // dispatch failure it set 'failed'. Writing status here would clobber
        // the edge fn's better state — just surface the error to the user.
        // fnErr.message is a fixed Supabase-client string ("Edge Function
        // returned a non-2xx status code") for every non-2xx response —
        // structurally incapable of carrying schedule-post's real reason.
        // The actual JSON body ({error, code}) only lives on fnErr.context.
        const realMessage = await ServiceError.extractEdgeFunctionMessage(fnErr, fnErr.message);
        // A 409 is a refusal that says what happened and what to do. "Publishing failed" would be its own false claim.
        if ((fnErr as { context?: Response }).context?.status === 409) {
          throw new ServiceError('PUBLISH_REFUSED', realMessage);
        }
        throw new ServiceError(
          'PUBLISH_FAILED',
          `Publishing to ${platformList} failed: ${realMessage}`,
        );
      }

      // The edge fn returns HTTP 202 even when every platform FAILED at dispatch
      // (status='failed') — 2xx alone is not success. Inspect the contract so we
      // never show "Posted" for a post that never went live. Held posts carry
      // status='pending_review' and are surfaced via `held` below.
      if (data?.status === 'failed') {
        throw new ServiceError(
          'PUBLISH_FAILED',
          `Publishing to ${platformList} failed. Open the post to see why, then try again.`,
        );
      }

      const held = data?.held === true;
      const results = Array.isArray(data?.results) ? data.results : undefined;

      // `results` describes the row's ENTIRE intended platform set, merged with
      // every prior attempt (edge fn: mergeEvidence(prior, fresh) over
      // intendedPlatforms). This call only ever dispatched `readyPlatforms`, so
      // a sibling platform's week-old evidence must never decide this action's
      // verdict — that let a failed Instagram publish resolve as a success
      // because LinkedIn had published days earlier, and it made every future
      // publish nag about a stale LinkedIn failure it never touched.
      const readyPlatformsSet = new Set<string>(readyPlatforms);
      const actionResults = results?.filter((r) => readyPlatformsSet.has(r.platform));

      // `skipped`/`scheduled` platforms are NOT failures — a partial-scope
      // "Post now" against a bigger row leaves the other platforms untouched
      // on purpose, and reporting those as failed would be its own false claim.
      const publishedPlatforms =
        actionResults?.filter((r) => r.status === 'published').map((r) => r.platform) ?? [];
      const failedPlatforms =
        actionResults
          ?.filter((r) => r.status === 'failed' && r.unconfirmed !== true)
          .map((r) => r.platform) ?? [];
      const unconfirmedPlatforms =
        actionResults
          ?.filter((r) => r.status === 'failed' && r.unconfirmed === true)
          .map((r) => r.platform) ?? [];

      const hasActionEvidence = (actionResults?.length ?? 0) > 0;
      const nothingDelivered = hasActionEvidence && publishedPlatforms.length === 0;

      // Dispatch ran and this action delivered nothing it can prove. The row may
      // still have rolled up to a non-`failed` partial status, so this guard —
      // not `status === 'failed'` — is what keeps a total non-delivery from
      // being reported as a success. `unconfirmed` is deliberately excluded: the
      // provider said yes and the post is very likely live, so claiming failure
      // there would be the inverse of the lie we are removing.
      if (!held && nothingDelivered && unconfirmedPlatforms.length === 0) {
        throw new ServiceError(
          'PUBLISH_FAILED',
          `Publishing to ${platformList} failed. Open the post to see why, then try again.`,
        );
      }

      const outcome: PublishOutcome = held
        ? 'held'
        : nothingDelivered && unconfirmedPlatforms.length > 0
          ? 'unconfirmed'
          : failedPlatforms.length > 0 || unconfirmedPlatforms.length > 0
            ? 'partial'
            : 'all_published';

      return {
        postId: data?.socialPostId || postId,
        platform: readyPlatforms[0],
        platforms: readyPlatforms,
        platformPostId: data?.platformPostId ?? undefined,
        publishedAt: new Date().toISOString(),
        held,
        outcome,
        results,
        // No per-platform evidence for the platforms this call asked for (an
        // older response shape, or a row whose evidence never mentions them) —
        // fall back to exactly what the caller requested, which is what the UI
        // already assumed before this contract existed.
        publishedPlatforms: hasActionEvidence ? publishedPlatforms : readyPlatforms,
        failedPlatforms,
        unconfirmedPlatforms,
        skippedUnreadyPlatforms,
      };
    }, 'Failed to publish post');
  }
}

export const publishService = PublishService.getInstance();
